import React, { useState, useRef, useEffect } from 'react';
import { FiUpload, FiFile, FiX } from 'react-icons/fi';

import useDeviceDetection from "./useDeviceDetection"

const DragDropUploader = ({ onFilesChange, clearFiles = false, acceptedTypes = ".pdf,.doc,.docx,.jpg,.jpeg,.png" }) => {
    const { isMobile, isDesktop } = useDeviceDetection();
    const [files, setFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Clear files when clearFiles prop changes
    useEffect(() => {
        if (clearFiles) {
            setFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [clearFiles]);

    // Update parent component when files change
    useEffect(() => {
        onFilesChange(files);
    }, [files]);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Only set isDragOver to false if we're actually leaving the drop zone
        // Check if the related target is outside the current target
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true); // Keep it blue while dragging over
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    };

    const handleFileInput = (e) => {
        const selectedFiles = Array.from(e.target.files);
        addFiles(selectedFiles);
    };

    const addFiles = (newFiles) => {
        const validFiles = newFiles.filter(file => {
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            return acceptedTypes.includes(fileExtension);
        });

        setFiles(prevFiles => [...prevFiles, ...validFiles]);
    };

    const removeFile = (indexToRemove) => {
        setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
            return '🖼️';
        } else if (['pdf'].includes(extension)) {
            return '📄';
        } else if (['doc', 'docx'].includes(extension)) {
            return '📝';
        }
        return '📁';
    };


    return (
        <div className="w-full">
            {/* Drop Zone */}
            <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${isDragOver
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                    }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <FiUpload className={`w-12 h-12 mx-auto mb-4 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className={`text-lg font-medium mb-2 ${isDragOver ? 'text-blue-600' : 'text-gray-600'}`}>
                    {isDesktop ?
                        <>{isDragOver ? 'Drop files here' : 'Drag & drop files here'}</>
                        :
                        "Click to browse your device"
                    }

                </p>
                <p className="text-sm text-gray-500 mb-4">
                    {isDesktop ? "or click to browse your computer" : "and add files"}
                </p>
                <p className="text-xs text-gray-400">
                    Supported: PDF, DOC, DOCX, JPG, JPEG, PNG
                </p>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={acceptedTypes}
                    onChange={handleFileInput}
                    className="hidden"
                />
            </div>

            {/* File List */}
            {files.length > 0 && (
                <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Selected Files ({files.length})
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                            >
                                <div className="flex items-center space-x-3">
                                    <span className="text-2xl">{getFileIcon(file.name)}</span>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatFileSize(file.size)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DragDropUploader;